import React from "react";
import styles from "./OiseauFond.module.css";
import {resoudre} from "../reglages";
import {REGLAGES} from "./reglages";
import {HAUTEUR, HAUTEUR_OISEAU, LARGEUR_CORPS, LARGEUR_EMBOUT, LARGEUR_OISEAU, PERIODE, SOL, XOISEAU, ambianceChoisie, centres, dureeBoucle, imagesClesVol, xTuyau} from "./parcours";

/*
 * Fond « Oiseau » : dans l'esprit de Flappy Bird, un petit oiseau qui traverse tout seul une série de tuyaux verts, au-dessus
 * d'un sol qui défile, avec des nuages et des collines en retrait. Dessin et code originaux (l'oiseau, les tuyaux et le décor
 * sont dessinés ici en SVG ; aucune image, aucun élément du jeu d'origine).
 * Les tuyaux, les collines et les nuages sont des bandes qui se répètent et glissent d'une période (un transform chacune) ;
 * l'oiseau suit un vol calculé par parcours.js (testé) : des sauts en arc (coup d'aile puis chute) qui le font passer au centre de chaque ouverture, plus un battement d'aile.
 * Une image de la galerie (propriété `images`, la première) peut remplacer l'oiseau.
 * Réglages (reglages.js) : vitesse, ouverture des tuyaux, taille de l'oiseau-image, ambiance (jour, soir, nuit).
 */

const VITESSE_BASE = 90;     // pixels par seconde à ×1

const AMBIANCES = {
    jour: {ciel: ["#4ec0ca", "#bdecf0"], nuage: "#ffffff", colline: "#8fd36b", tuyau: ["#4f9a1f", "#9be45a", "#4f9a1f"], contour: "#33590f", herbeA: "#8fd33f", herbeB: "#6fb52c", terre: "#d9c283"},
    soir: {ciel: ["#6a4a9c", "#f8a56a"], nuage: "#ffc9a6", colline: "#5b3f85", tuyau: ["#3d7a2b", "#7fc060", "#3d7a2b"], contour: "#274a1c", herbeA: "#6fa04a", herbeB: "#558238", terre: "#9a6f52"},
    nuit: {ciel: ["#0b1a3a", "#27518a"], nuage: "#3b5a93", colline: "#14305a", tuyau: ["#1f6a55", "#43b090", "#1f6a55"], contour: "#0e3328", herbeA: "#2f8a6a", herbeB: "#1f6a50", terre: "#3b4468"},
};

const OUVERTURE_CAP = 12;    // hauteur de l'embout d'un tuyau

/** Générateur pseudo-aléatoire à graine (mulberry32). */
function aleatoire(graine) {
    let a = graine >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Une bande qui se répète : son contenu est dessiné deux fois (deux périodes), elle glisse d'une période. */
const Bande = ({duree, children}) => (
    <svg className={styles.bande} width={PERIODE * 2} height={HAUTEUR} style={{animationDuration: `${duree.toFixed(1)}s`}}>
        {[0, 1].map(m => <g key={m} transform={`translate(${m * PERIODE} 0)`}>{children}</g>)}
    </svg>
);

function Tuyaux({centresOuvertures, ouverture, couleurs, idDegrade}) {
    const sol = HAUTEUR - SOL;
    return centresOuvertures.map((c, k) => {
        const x = xTuyau(k), haut = c - ouverture / 2, bas = c + ouverture / 2;
        const style = {fill: `url(#${idDegrade})`, stroke: couleurs.contour, strokeWidth: 1.5};
        return (
            <g key={k}>
                <rect x={x - LARGEUR_CORPS / 2} y={-2} width={LARGEUR_CORPS} height={haut - OUVERTURE_CAP + 2} style={style}/>
                <rect x={x - LARGEUR_EMBOUT / 2} y={haut - OUVERTURE_CAP} width={LARGEUR_EMBOUT} height={OUVERTURE_CAP} style={style}/>
                <rect x={x - LARGEUR_EMBOUT / 2} y={bas} width={LARGEUR_EMBOUT} height={OUVERTURE_CAP} style={style}/>
                <rect x={x - LARGEUR_CORPS / 2} y={bas + OUVERTURE_CAP} width={LARGEUR_CORPS} height={sol - bas - OUVERTURE_CAP + 2} style={style}/>
            </g>
        );
    });
}

/** Collines : somme de deux ondes à nombre entier de cycles par période, donc sans raccord. */
function Collines({couleur}) {
    const pas = 40, pts = [];
    for (let x = 0; x <= PERIODE; x += pas) {
        const u = (2 * Math.PI * x) / PERIODE;
        pts.push(`${x},${(74 - 12 * Math.sin(5 * u + 1) - 6 * Math.sin(11 * u + 2)).toFixed(1)}`);
    }
    return <path d={`M${pts.join("L")}L${PERIODE},${HAUTEUR}L0,${HAUTEUR}Z`} fill={couleur}/>;
}

/** Nuages : quelques bouquets d'ellipses, tirés avec une graine fixe. */
function Nuages({couleur}) {
    const r = aleatoire(777);
    return Array.from({length: 7}, (_, i) => {
        const x = (i + 0.2 + r() * 0.6) * (PERIODE / 7), y = 12 + r() * 30, e = 0.8 + r() * 0.8;
        return (
            <g key={i} fill={couleur} opacity="0.9">
                <ellipse cx={x} cy={y} rx={22 * e} ry={9 * e}/>
                <ellipse cx={x - 14 * e} cy={y + 3 * e} rx={14 * e} ry={7 * e}/>
                <ellipse cx={x + 16 * e} cy={y + 2 * e} rx={16 * e} ry={7 * e}/>
            </g>
        );
    });
}

const Oiseau = () => (
    <svg width="24" height="16" viewBox="0 0 24 16" overflow="visible">
        <ellipse cx="11" cy="8" rx="10" ry="7" fill="#ffd23f" stroke="#4a3200" strokeWidth="1.2"/>
        <ellipse cx="12" cy="11" rx="6" ry="3" fill="#fff0a8"/>
        <polygon points="19,7 24,8.5 19,11" fill="#f4772e" stroke="#4a3200" strokeWidth="0.8"/>
        <circle cx="15.5" cy="5.5" r="2.6" fill="#fff" stroke="#4a3200" strokeWidth="0.8"/>
        <circle cx="16.3" cy="5.7" r="1.1" fill="#222"/>
        <g className={styles.aile}><ellipse cx="7.5" cy="9" rx="5" ry="3.2" fill="#f0a21a" stroke="#4a3200" strokeWidth="0.9"/></g>
    </svg>
);

export default function OiseauFond({reglages, images = [], horaires}) {
    const {vitesse, ouverture, ambiance, taille} = resoudre(REGLAGES, reglages);
    // image de la galerie à la place de l'oiseau (la première) : son rapport largeur / hauteur n'est connu qu'une fois chargée ;
    // en attendant, ou si elle échoue, c'est l'oiseau d'origine
    const adresse = images[0];
    const [lue, setLue] = React.useState(null);
    const etat = adresse && lue && lue.adresse === adresse ? lue : null;
    const image = etat && !etat.hs ? adresse : null;
    const hauteurOiseau = image ? taille : HAUTEUR_OISEAU;
    const largeurOiseau = image ? Math.round(taille * etat.rapport) : LARGEUR_OISEAU;
    // « auto » se recalcule à chaque affichage du bandeau (une fois par seconde) : l'ambiance change à l'heure dite
    const nomAmbiance = ambianceChoisie(ambiance, new Date(), horaires);
    const a = AMBIANCES[nomAmbiance];
    const v = VITESSE_BASE * vitesse;
    const liste = centres(ouverture);
    const nomVol = `oiseauVol${ouverture}`;
    const idDegrade = `oiseau-tuyau-${nomAmbiance}`;

    return (
        <div className={styles.scene} aria-hidden="true" style={{background: `linear-gradient(180deg, ${a.ciel[0]} 0%, ${a.ciel[1]} 100%)`}}>
            <style>{imagesClesVol(nomVol, liste, ouverture)}</style>
            <svg width="0" height="0" className={styles.defs}>
                <defs>
                    <linearGradient id={idDegrade} x1="0" y1="0" x2="1" y2="0">
                        {a.tuyau.map((c, i) => <stop key={i} offset={i / (a.tuyau.length - 1)} stopColor={c}/>)}
                    </linearGradient>
                </defs>
            </svg>

            <Bande duree={dureeBoucle(v * 0.1)}><Nuages couleur={a.nuage}/></Bande>
            <Bande duree={dureeBoucle(v * 0.3)}><Collines couleur={a.colline}/></Bande>
            <Bande duree={dureeBoucle(v)}><Tuyaux centresOuvertures={liste} ouverture={ouverture} couleurs={a} idDegrade={idDegrade}/></Bande>

            <div className={styles.sol} style={{
                height: SOL,
                animationDuration: `${(24 / v).toFixed(3)}s`,
                background: `repeating-linear-gradient(90deg, ${a.herbeA} 0 12px, ${a.herbeB} 12px 24px) top / 100% 5px no-repeat, ${a.terre}`,
            }}/>

            {/* le vol place le haut d'une boîte de HAUTEUR_OISEAU : une image plus haute est recentrée par la marge */}
            <div
                className={styles.oiseau}
                style={{
                    left: XOISEAU - largeurOiseau / 2,
                    width: largeurOiseau,
                    height: hauteurOiseau,
                    marginTop: (HAUTEUR_OISEAU - hauteurOiseau) / 2,
                    animationName: nomVol,
                    animationDuration: `${dureeBoucle(v).toFixed(1)}s`,
                }}
            >
                {image ? <img src={image} alt="" className={styles.image}/> : <Oiseau/>}
            </div>
            {adresse && (!etat || image) && (
                <img
                    src={adresse}
                    alt=""
                    style={{display: "none"}}
                    onLoad={ev => setLue({adresse, rapport: ev.target.naturalHeight ? ev.target.naturalWidth / ev.target.naturalHeight : 1})}
                    onError={() => setLue({adresse, hs: true})}
                />
            )}
        </div>
    );
}
